import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../../App';
import { useCurrency } from '../../contexts/CurrencyContext';
import Layout from '../../components/Layout';
import AdDisplay from '../../components/AdDisplay';
import Price from '../../components/Price';
import ShareButton, { getOGShareUrl } from '../../components/ShareButton';
import NotificationBanner from '../../components/NotificationBanner';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ExternalLink, Star, Heart, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Bell, BellOff, Crown, CreditCard } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TipServices = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { formatPrice } = useCurrency();
  const [services, setServices] = useState([]);
  const [allServices, setAllServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('random');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [likedItems, setLikedItems] = useState(new Set());
  const [followedServices, setFollowedServices] = useState(new Set());
  const [reviewStats, setReviewStats] = useState({});
  const [subscribedServices, setSubscribedServices] = useState(new Set());
  const [paymentSettings, setPaymentSettings] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [subscribing, setSubscribing] = useState(false);
  const ITEMS_PER_PAGE = 12;

  useEffect(() => {
    fetchServices();
    fetchPaymentSettings();
    if (user) {
      fetchUserLikes();
      fetchFollowedServices();
    }
  }, [user]);

  // Refetch follow state when navigating back to this page
  useEffect(() => {
    if (user) {
      fetchFollowedServices();
    }
  }, [location.pathname, user]);

  useEffect(() => {
    if (user && allServices.length > 0) {
      fetchSubscriptions();
    }
  }, [user, allServices]);

  const fetchPaymentSettings = async () => {
    try {
      const response = await axios.get(`${API}/payment-settings-public`);
      setPaymentSettings(response.data);
      if (response.data.stripe?.enabled) {
        setPaymentMethod('stripe');
      } else if (response.data.paypal?.enabled) {
        setPaymentMethod('paypal');
      }
    } catch (error) {
      console.error('Error fetching payment settings:', error);
    }
  };

  const fetchSubscriptions = async () => {
    const token = localStorage.getItem('session_token');
    try {
      const promises = allServices.map(service =>
        axios.get(`${API}/tipster-services/${service.id}/subscription-status`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: { subscribed: false } }))
      );
      const results = await Promise.all(promises);
      const subscribed = new Set();
      results.forEach((result, idx) => {
        if (result.data.subscribed) {
          subscribed.add(allServices[idx].id);
        }
      });
      setSubscribedServices(subscribed);
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
    }
  };

  useEffect(() => {
    sortAndPaginateServices();
  }, [sortBy, currentPage, allServices, reviewStats]);

  // Re-fetch likes when page becomes visible (user navigates back)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && user) {
        fetchUserLikes();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user]);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/tipster-services`);
      const servicesData = response.data;
      setAllServices(servicesData);

      // Fetch review stats for each service
      const statsPromises = servicesData.map(service =>
        axios.get(`${API}/reviews/tipster_service/${service.id}`).catch(() => ({ data: { average_rating: 0, total_reviews: 0 } }))
      );
      const statsResults = await Promise.all(statsPromises);
      const stats = {};
      servicesData.forEach((service, idx) => {
        stats[service.id] = statsResults[idx].data;
      });
      setReviewStats(stats);
      setCurrentPage(1);
    } catch (error) {
      console.error('Error fetching services:', error);
      toast.error('Failed to load tip services');
    } finally {
      setLoading(false);
    }
  };

  const sortAndPaginateServices = () => {
    let sorted = [...allServices];

    // Sort
    switch (sortBy) {
      case 'newest':
        sorted.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        break;
      case 'popularity':
        sorted.sort((a, b) => {
          const aLikes = likedItems.has(a.id) ? 1 : 0;
          const bLikes = likedItems.has(b.id) ? 1 : 0;
          return bLikes - aLikes;
        });
        break;
      case 'best_review':
        sorted.sort((a, b) => {
          const aRating = reviewStats[a.id]?.average_rating || 0;
          const bRating = reviewStats[b.id]?.average_rating || 0;
          return bRating - aRating;
        });
        break;
      case 'random':
      default:
        for (let i = sorted.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [sorted[i], sorted[j]] = [sorted[j], sorted[i]];
        }
        break;
    }

    // Paginate
    const total = Math.ceil(sorted.length / ITEMS_PER_PAGE);
    setTotalPages(total);
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    const end = start + ITEMS_PER_PAGE;
    setServices(sorted.slice(start, end));
  };

  const fetchUserLikes = async () => {
    const token = localStorage.getItem('session_token');
    try {
      const response = await axios.get(`${API}/likes/user`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const liked = new Set(response.data.tipster_services.map(s => s.id));
      setLikedItems(liked);
    } catch (error) {
      console.error('Error fetching likes:', error);
    }
  };

  const handleToggleLike = async (e, serviceId) => {
    e.stopPropagation();
    if (!user) {
      toast.error('Please login to like services');
      navigate('/login');
      return;
    }

    const token = localStorage.getItem('session_token');
    try {
      const response = await axios.post(
        `${API}/likes/toggle?item_id=${serviceId}&item_type=tipster_service`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.liked) {
        setLikedItems(prev => new Set([...prev, serviceId]));
      } else {
        setLikedItems(prev => {
          const newSet = new Set(prev);
          newSet.delete(serviceId);
          return newSet;
        });
      }
      toast.success(response.data.message);
    } catch (error) {
      toast.error('Failed to update favorite');
    }
  };

  const fetchFollowedServices = async () => {
    if (!user) return;
    
    const token = localStorage.getItem('session_token');
    try {
      // Get user data with followed services
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const followed = new Set(response.data.followed_tip_services || []);
      setFollowedServices(followed);
    } catch (error) {
      console.error('Error fetching followed services:', error);
    }
  };

  const handleToggleFollow = async (e, serviceId) => {
    e.stopPropagation();
    
    if (!user) {
      navigate('/login');
      return;
    }

    const token = localStorage.getItem('session_token');
    
    try {
      // Use toggle endpoint
      const response = await axios.post(`${API}/tipster-services/${serviceId}/follow`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update local state based on response
      if (response.data.is_following) {
        setFollowedServices(prev => new Set([...prev, serviceId]));
        toast.success('Following service! You\'ll receive email notifications for new posts.');
      } else {
        setFollowedServices(prev => {
          const newSet = new Set(prev);
          newSet.delete(serviceId);
          return newSet;
        });
        toast.success('Unfollowed service');
      }
    } catch (error) {
      toast.error('Failed to update follow status');
    }
  };

  const handleSubscribe = (e, service) => {
    e.stopPropagation();
    if (!user) {
      toast.error('Please login to subscribe');
      return;
    }
    setSelectedService(service);
    setShowPaymentModal(true);
  };

  const confirmSubscription = async () => {
    if (!selectedService) return;
    
    setSubscribing(true);
    setShowPaymentModal(false);
    
    const token = localStorage.getItem('session_token');
    const originUrl = window.location.origin;
    
    try {
      const endpoint = paymentMethod === 'stripe'
        ? `/payments/service-subscription/stripe/checkout?service_id=${selectedService.id}&origin_url=${encodeURIComponent(originUrl)}`
        : `/payments/service-subscription/paypal/checkout?service_id=${selectedService.id}&origin_url=${encodeURIComponent(originUrl)}`;
      
      const response = await axios.post(`${API}${endpoint}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const redirectUrl = response.data.url || response.data.approval_url;
      if (redirectUrl) {
        window.location.href = redirectUrl;
      } else {
        toast.error('Payment URL not received');
        setSubscribing(false);
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast.error(error.response?.data?.detail || 'Failed to start payment');
      setSubscribing(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-white text-lg">Loading tip services...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="tip-services-page" className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">
          {/* Notification Banner */}
          <NotificationBanner type="services" />

          <div className="mb-8">
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">Featured Tip Services</h1>
            <p className="text-gray-300">Discover professional tipsters and their services</p>
          </div>

          {/* Sort Dropdown */}
          <div className="flex justify-end items-center gap-2 mb-6">
            <span className="text-sm text-gray-400 whitespace-nowrap">Sort by:</span>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-40 bg-white/5 border-white/10 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="random">Random</SelectItem>
                <SelectItem value="newest">Newest</SelectItem>
                <SelectItem value="popularity">Popularity</SelectItem>
                <SelectItem value="best_review">Best Review</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {services.length === 0 ? (
            <Card className="glass-dark border-white/10">
              <CardContent className="py-12 text-center">
                <p className="text-gray-400 text-lg">No tip services available yet</p>
                <p className="text-gray-500 text-sm mt-2">Be the first tipster to list your service!</p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Services 1-3 */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {services.slice(0, 3).map((service) => {
                  const stats = reviewStats[service.id] || { average_rating: 0, total_reviews: 0 };
                  const isLiked = likedItems.has(service.id);
                  const isFollowing = followedServices.has(service.id);

                  return (
                    <Card key={service.id} className="glass-dark border-white/10 card-hover">
                      <CardContent className="pt-6">
                        {/* Rating Display - Top Right */}
                        <div className="flex items-center justify-end mb-3">
                          <div className="flex items-center space-x-1">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Star
                                key={star}
                                className={`w-4 h-4 ${
                                  star <= Math.round(stats.average_rating)
                                    ? 'fill-yellow-400 text-yellow-400'
                                    : 'text-gray-600'
                                }`}
                              />
                            ))}
                            <span className="text-sm font-semibold text-white ml-1">
                              {stats.average_rating > 0 ? stats.average_rating.toFixed(1) : '0.0'}
                            </span>
                          </div>
                        </div>

                        {service.image_url && (
                          <div className="mb-4 rounded-lg overflow-hidden">
                            <img src={service.image_url} alt={service.name} className="w-full h-48 object-cover" />
                          </div>
                        )}
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <h3 
                              className="text-xl font-bold text-white cursor-pointer hover:text-blue-400 transition-colors"
                              onClick={() => navigate(`/tip-services/${service.id}`)}
                            >
                              {service.name}
                            </h3>
                            {service.is_featured && <Star className="w-5 h-5 text-amber-400 fill-amber-400" />}
                          </div>
                          <div className="flex gap-2">
                            <Button
                              onClick={(e) => handleToggleFollow(e, service.id)}
                              size="sm"
                              className={`${isFollowing ? 'bg-blue-600 hover:bg-blue-700' : 'bg-white/5 hover:bg-white/10 border border-white/20'} text-white`}
                            >
                              {isFollowing ? (
                                <>
                                  <Bell className="w-4 h-4 mr-1" />
                                  Following
                                </>
                              ) : (
                                <>
                                  <BellOff className="w-4 h-4 mr-1" />
                                  Follow
                                </>
                              )}
                            </Button>
                            <Button
                              onClick={(e) => handleToggleLike(e, service.id)}
                              variant="ghost"
                              size="sm"
                              className={`${isLiked ? 'text-red-400' : 'text-gray-400'} hover:text-red-400 p-1`}
                            >
                              <Heart className={`w-5 h-5 ${isLiked ? 'fill-red-400' : ''}`} />
                            </Button>
                          </div>
                        </div>

                        <p className="text-lg text-blue-400 mb-3">{service.title}</p>
                        <p className="text-gray-300 text-sm line-clamp-3 mb-4">{service.description}</p>
                        {service.subscription_price > 0 ? (
                          <div className="mb-4">
                            <span className="text-green-400 font-bold text-lg">
                              <Price amount={service.subscription_price} />/month
                            </span>
                          </div>
                        ) : (
                          <div className="mb-4">
                            <span className="text-green-400 font-bold text-lg">Free</span>
                          </div>
                        )}
                        
                        {/* Subscription Button - only show if price > 0 */}
                        {service.subscription_price > 0 && (
                          subscribedServices.has(service.id) ? (
                            <div className="mb-3 bg-green-900/20 border border-green-500/30 rounded p-2 text-center">
                              <p className="text-green-400 text-sm font-semibold flex items-center justify-center gap-1">
                                <Crown className="w-4 h-4" />
                                Subscribed
                              </p>
                            </div>
                          ) : (
                            <Button
                              onClick={(e) => handleSubscribe(e, service)}
                              className="w-full mb-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-sm"
                              disabled={subscribing}
                            >
                              <Crown className="w-4 h-4 mr-1" />
                              Subscribe {formatPrice(service.subscription_price)}/mo
                            </Button>
                          )
                        )}
                        
                        <div className="flex gap-2">
                          <Button 
                            onClick={() => navigate(`/tip-services/${service.id}`)}
                            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                          >
                            View Details <ExternalLink className="w-4 h-4 ml-2" />
                          </Button>
                          <ShareButton 
                            shareUrl={getOGShareUrl('tip-services', service.id)}
                            title={service.name}
                            description={`Check out ${service.name} on BetBanky - ${service.title}`}
                          />
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              {/* Ad Row 1 - Show if 3+ services exist */}
              {services.length >= 3 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                  <AdDisplay page="tips" position="row1_ad1" />
                  <AdDisplay page="tips" position="row1_ad2" />
                </div>
              )}

              {/* Services 4-9 */}
              {services.length > 3 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
                  {services.slice(3, 9).map((service) => {
                    const stats = reviewStats[service.id] || { average_rating: 0, total_reviews: 0 };
                    const isLiked = likedItems.has(service.id);
                    const isFollowing = followedServices.has(service.id);

                    return (
                      <Card key={service.id} className="glass-dark border-white/10 card-hover">
                        <CardContent className="pt-6">
                          {/* Rating Display - Top Right */}
                          <div className="flex items-center justify-end mb-3">
                            <div className="flex items-center space-x-1">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <Star
                                  key={star}
                                  className={`w-4 h-4 ${
                                    star <= Math.round(stats.average_rating)
                                      ? 'fill-yellow-400 text-yellow-400'
                                      : 'text-gray-600'
                                  }`}
                                />
                              ))}
                              <span className="text-sm font-semibold text-white ml-1">
                                {stats.average_rating > 0 ? stats.average_rating.toFixed(1) : '0.0'}
                              </span>
                            </div>
                          </div>

                          {service.image_url && (
                            <div className="mb-4 rounded-lg overflow-hidden">
                              <img src={service.image_url} alt={service.name} className="w-full h-48 object-cover" />
                            </div>
                          )}
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <h3 
                                className="text-xl font-bold text-white cursor-pointer hover:text-blue-400 transition-colors"
                                onClick={() => navigate(`/tip-services/${service.id}`)}
                              >
                                {service.name}
                              </h3>
                              {service.is_featured && <Star className="w-5 h-5 text-amber-400 fill-amber-400" />}
                            </div>
                            <div className="flex gap-2">
                              <Button
                                onClick={(e) => handleToggleFollow(e, service.id)}
                                size="sm"
                                className={`${isFollowing ? 'bg-blue-600 hover:bg-blue-700' : 'bg-white/5 hover:bg-white/10 border border-white/20'} text-white`}
                              >
                                {isFollowing ? (
                                  <>
                                    <Bell className="w-4 h-4 mr-1" />
                                    Following
                                  </>
                                ) : (
                                  <>
                                    <BellOff className="w-4 h-4 mr-1" />
                                    Follow
                                  </>
                                )}
                              </Button>
                              <Button
                                onClick={(e) => handleToggleLike(e, service.id)}
                                variant="ghost"
                                size="sm"
                                className={`${isLiked ? 'text-red-400' : 'text-gray-400'} hover:text-red-400 p-1`}
                              >
                                <Heart className={`w-5 h-5 ${isLiked ? 'fill-red-400' : ''}`} />
                              </Button>
                            </div>
                          </div>

                          <p className="text-lg text-blue-400 mb-3">{service.title}</p>
                          <p className="text-gray-300 text-sm line-clamp-3 mb-4">{service.description}</p>
                          {service.subscription_price > 0 ? (
                            <div className="mb-4">
                              <span className="text-green-400 font-bold text-lg">
                                <Price amount={service.subscription_price} />/month
                              </span>
                            </div>
                          ) : (
                            <div className="mb-4">
                              <span className="text-green-400 font-bold text-lg">Free</span>
                            </div>
                          )}
                          
                          {service.subscription_price > 0 && (
                            subscribedServices.has(service.id) ? (
                              <div className="mb-3 bg-green-900/20 border border-green-500/30 rounded p-2 text-center">
                                <p className="text-green-400 text-sm font-semibold flex items-center justify-center gap-1">
                                  <Crown className="w-4 h-4" />
                                  Subscribed
                                </p>
                              </div>
                            ) : (
                              <Button
                                onClick={(e) => handleSubscribe(e, service)}
                                className="w-full mb-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-sm"
                                disabled={subscribing}
                              >
                                <Crown className="w-4 h-4 mr-1" />
                                Subscribe {formatPrice(service.subscription_price)}/mo
                              </Button>
                            )
                          )}
                          
                          <div className="flex gap-2">
                            <Button 
                              onClick={() => navigate(`/tip-services/${service.id}`)}
                              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                            >
                              View Details <ExternalLink className="w-4 h-4 ml-2" />
                            </Button>
                            <ShareButton 
                              shareUrl={getOGShareUrl('tip-services', service.id)}
                              title={service.name}
                              description={`Check out ${service.name} on BetBanky - ${service.title}`}
                            />
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}

              {/* Ad Row 2 - Show if 9+ services exist (after 9th item) */}
              {services.length >= 9 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                  <AdDisplay page="tips" position="row2_ad1" />
                  <AdDisplay page="tips" position="row2_ad2" />
                </div>
              )}

              {/* Remaining Services (10+) */}
              {services.length > 9 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
                  {services.slice(9).map((service) => {
                    const stats = reviewStats[service.id] || { average_rating: 0, total_reviews: 0 };
                    const isLiked = likedItems.has(service.id);
                    const isFollowing = followedServices.has(service.id);

                    return (
                      <Card key={service.id} className="glass-dark border-white/10 card-hover">
                        <CardContent className="pt-6">
                          {/* Rating Display - Top Right */}
                          <div className="flex items-center justify-end mb-3">
                            <div className="flex items-center space-x-1">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <Star
                                  key={star}
                                  className={`w-4 h-4 ${
                                    star <= Math.round(stats.average_rating)
                                      ? 'fill-yellow-400 text-yellow-400'
                                      : 'text-gray-600'
                                  }`}
                                />
                              ))}
                              <span className="text-sm font-semibold text-white ml-1">
                                {stats.average_rating > 0 ? stats.average_rating.toFixed(1) : '0.0'}
                              </span>
                            </div>
                          </div>

                          {service.image_url && (
                            <div className="mb-4 rounded-lg overflow-hidden">
                              <img src={service.image_url} alt={service.name} className="w-full h-48 object-cover" />
                            </div>
                          )}
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <h3 
                                className="text-xl font-bold text-white cursor-pointer hover:text-blue-400 transition-colors"
                                onClick={() => navigate(`/tip-services/${service.id}`)}
                              >
                                {service.name}
                              </h3>
                              {service.is_featured && <Star className="w-5 h-5 text-amber-400 fill-amber-400" />}
                            </div>
                            <div className="flex gap-2">
                              <Button
                                onClick={(e) => handleToggleFollow(e, service.id)}
                                size="sm"
                                className={`${isFollowing ? 'bg-blue-600 hover:bg-blue-700' : 'bg-white/5 hover:bg-white/10 border border-white/20'} text-white`}
                              >
                                {isFollowing ? (
                                  <>
                                    <Bell className="w-4 h-4 mr-1" />
                                    Following
                                  </>
                                ) : (
                                  <>
                                    <BellOff className="w-4 h-4 mr-1" />
                                    Follow
                                  </>
                                )}
                              </Button>
                              <Button
                                onClick={(e) => handleToggleLike(e, service.id)}
                                variant="ghost"
                                size="sm"
                                className={`${isLiked ? 'text-red-400' : 'text-gray-400'} hover:text-red-400 p-1`}
                              >
                                <Heart className={`w-5 h-5 ${isLiked ? 'fill-red-400' : ''}`} />
                              </Button>
                            </div>
                          </div>

                          <p className="text-lg text-blue-400 mb-3">{service.title}</p>
                          <p className="text-gray-300 text-sm line-clamp-3 mb-4">{service.description}</p>
                          {service.subscription_price > 0 ? (
                            <div className="mb-4">
                              <span className="text-green-400 font-bold text-lg">
                                <Price amount={service.subscription_price} />/month
                              </span>
                            </div>
                          ) : (
                            <div className="mb-4">
                              <span className="text-green-400 font-bold text-lg">Free</span>
                            </div>
                          )}
                          
                          {service.subscription_price > 0 && (
                            subscribedServices.has(service.id) ? (
                              <div className="mb-3 bg-green-900/20 border border-green-500/30 rounded p-2 text-center">
                                <p className="text-green-400 text-sm font-semibold flex items-center justify-center gap-1">
                                  <Crown className="w-4 h-4" />
                                  Subscribed
                                </p>
                              </div>
                            ) : (
                              <Button
                                onClick={(e) => handleSubscribe(e, service)}
                                className="w-full mb-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-sm"
                                disabled={subscribing}
                              >
                                <Crown className="w-4 h-4 mr-1" />
                                Subscribe {formatPrice(service.subscription_price)}/mo
                              </Button>
                            )
                          )}
                          
                          <div className="flex gap-2">
                            <Button 
                              onClick={() => navigate(`/tip-services/${service.id}`)}
                              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                            >
                              View Details <ExternalLink className="w-4 h-4 ml-2" />
                            </Button>
                            <ShareButton 
                              shareUrl={getOGShareUrl('tip-services', service.id)}
                              title={service.name}
                              description={`Check out ${service.name} on BetBanky - ${service.title}`}
                            />
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}

              {/* Ad Row 2 at bottom - Show if fewer than 9 services but more than 0 */}
              {services.length > 0 && services.length < 9 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                  <AdDisplay page="tips" position="row2_ad1" />
                  <AdDisplay page="tips" position="row2_ad2" />
                </div>
              )}
            </>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-2 mt-8 flex-wrap">
              <Button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                variant="outline"
                size="sm"
                className="border-white/20 text-white hover:bg-white/10 disabled:opacity-50"
              >
                <ChevronsLeft className="w-4 h-4" />
              </Button>
              <Button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                variant="outline"
                size="sm"
                className="border-white/20 text-white hover:bg-white/10 disabled:opacity-50"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>

              {/* Page Numbers */}
              {(() => {
                const pages = [];
                const start = Math.max(1, currentPage - 2);
                const end = Math.min(totalPages, currentPage + 2);

                for (let i = start; i <= end; i++) {
                  pages.push(
                    <Button
                      key={i}
                      onClick={() => setCurrentPage(i)}
                      variant={currentPage === i ? 'default' : 'outline'}
                      size="sm"
                      className={currentPage === i 
                        ? 'bg-blue-600 hover:bg-blue-700' 
                        : 'border-white/20 text-white hover:bg-white/10'}
                    >
                      {i}
                    </Button>
                  );
                }
                return pages;
              })()}

              <Button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                variant="outline"
                size="sm"
                className="border-white/20 text-white hover:bg-white/10 disabled:opacity-50"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
              <Button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                variant="outline"
                size="sm"
                className="border-white/20 text-white hover:bg-white/10 disabled:opacity-50"
              >
                <ChevronsRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Payment Modal */}
      {showPaymentModal && selectedService && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="glass-dark border-white/10 max-w-md w-full">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white text-xl font-bold flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  Subscribe to Service
                </h3>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setShowPaymentModal(false)}
                  className="text-white hover:bg-white/10"
                >
                  ✕
                </Button>
              </div>
              
              <div className="bg-white/5 rounded p-4 mb-4">
                <p className="text-gray-400 text-sm">Monthly Subscription</p>
                <p className="text-3xl font-bold text-purple-400 mb-2">
                  {formatPrice(selectedService.subscription_price)}
                </p>
                <p className="text-gray-400 text-sm">Unlimited access to all tips</p>
              </div>
              
              <div className="mb-4">
                <p className="text-white font-semibold mb-3">Payment Method:</p>
                <div className="space-y-2">
                  {paymentSettings?.stripe?.enabled && (
                    <button
                      onClick={() => setPaymentMethod('stripe')}
                      className={`w-full p-4 rounded-lg border-2 transition-all ${
                        paymentMethod === 'stripe'
                          ? 'border-blue-500 bg-blue-500/20'
                          : 'border-white/10 bg-white/5 hover:border-white/30'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          paymentMethod === 'stripe' ? 'border-blue-500' : 'border-white/30'
                        }`}>
                          {paymentMethod === 'stripe' && (
                            <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                          )}
                        </div>
                        <span className="text-white font-medium">Credit/Debit Card (Stripe)</span>
                      </div>
                    </button>
                  )}
                  
                  {paymentSettings?.paypal?.enabled && (
                    <button
                      onClick={() => setPaymentMethod('paypal')}
                      className={`w-full p-4 rounded-lg border-2 transition-all ${
                        paymentMethod === 'paypal'
                          ? 'border-blue-500 bg-blue-500/20'
                          : 'border-white/10 bg-white/5 hover:border-white/30'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          paymentMethod === 'paypal' ? 'border-blue-500' : 'border-white/30'
                        }`}>
                          {paymentMethod === 'paypal' && (
                            <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                          )}
                        </div>
                        <span className="text-white font-medium">PayPal</span>
                      </div>
                    </button>
                  )}
                </div>
              </div>
              
              <Button
                onClick={confirmSubscription}
                className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 w-full text-lg py-6"
              >
                Proceed to Payment
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
    </Layout>
  );
};

export default TipServices;
